import {useCallback, useEffect, useState} from 'react'
import useAsync from '../../commerce-api/hooks/useAsync'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useBazaarvoiceAPI} from '../context'
import {BV_REVIEWS_LIMIT} from '../../constants'
import useMultiSite from '../../hooks/use-multi-site'
import {localeToBVLocale} from '../utils'

/**
 * Hook to fetch product reviews for a given product id and query with pagination
 * @param {string} productId
 * @param {boolean} [active] Whether to fetch reviews or not
 * @param {number[]} [rating] Filters to apply to the query
 * @param {number} [pageSize] Page size to apply to the query
 * @param {string} [searchTerm] Search term to apply to the query
 * @param {string} [sort] Sort to apply to the query
 * @returns {{
 *   loadNextPage: (function(): void),
 *   reviews: object[],
 *   totalCount: number|null,
 *   error: Error|null,
 *   loading: boolean
 * }}
 */
const useProductReviews = (
    productId,
    {enabled = true, rating, pageSize = BV_REVIEWS_LIMIT, searchTerm, sort, language} = {}
) => {
    const {locale} = useMultiSite()
    const {bvEnabled} = useOcapiSite()
    const {getProductReviews} = useBazaarvoiceAPI()
    const [query, setQuery] = useState()
    const [offset, setOffset] = useState(0)
    const [reviews, setReviews] = useState([])
    const [totalCount, setTotalCount] = useState(null)
    const {data, error, loading, execute, reset} = useAsync(getProductReviews)
    const isEnabled = productId && bvEnabled && enabled

    // execute the async function with the provided parameters and abort controller
    const _execute = useCallback(
        (productId, query) => {
            const abortController = new AbortController()
            execute(productId, {...query, signal: abortController.signal})
            return () => {
                abortController.abort()
                reset()
            }
        },
        [execute, reset]
    )

    // merge new reviews with existing ones
    useEffect(() => {
        if (!data || loading || error) return
        const {reviews, totalCount} = data
        setReviews((prev) => [...prev, ...reviews])
        setTotalCount(totalCount)
    }, [data, loading, error])

    // update query when the params change
    useEffect(() => {
        if (!isEnabled) {
            return
        }

        setQuery({
            Locale: localeToBVLocale(locale),
            Limit: pageSize,
            rating,
            Search: searchTerm,
            Sort: sort,
            contentLocale: language ? locale.id.split('-')[0] : ''
        })
        setOffset(0)
        setReviews([])
        setTotalCount(null)
        reset()
    }, [isEnabled, locale, pageSize, productId, rating, reset, searchTerm, sort, language])

    const loadNextPage = useCallback(() => {
        if (!isEnabled || loading || error) {
            return
        }

        setOffset((o) => o + pageSize)
    }, [error, isEnabled, loading, pageSize])

    // trigger the search when query or offset changes
    useEffect(() => {
        if (!isEnabled) {
            return
        }

        // skip the first trigger before the effect to fill the query to avoid an extra request
        if (!query) {
            return
        }

        return _execute(productId, {
            ...query,
            Offset: offset
        })
    }, [_execute, isEnabled, offset, query, productId])

    return {
        error,
        loading,
        loadNextPage,
        reviews,
        totalCount
    }
}

export default useProductReviews
